import { useState } from "react";
import { queryAuditLog } from "../api/audit";
import type { AuditLogFilters } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { Pagination } from "../components/Pagination";
import { formatBeijingTime } from "../utils/datetime";

const RESULTS = ["SUCCESS", "FAILURE", "PARTIAL"];
const PAGE_SIZE = 50;

const EMPTY_FILTERS: AuditLogFilters = {};

/**
 * 审计日志查询页,对应产品文档 §10.1(三种角色均可查看)。
 *
 * 支持按操作对象/动作/操作人(子串匹配)、结果(精确匹配)自由组合查询,
 * 分页展示,查询范围覆盖全部历史记录(不再局限于当月)。
 */
export function AuditLogPage() {
  const [filterDraft, setFilterDraft] = useState<AuditLogFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<AuditLogFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const logs = useAsync(
    () => queryAuditLog({ ...appliedFilters, page, pageSize: PAGE_SIZE }),
    [appliedFilters, page]
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedFilters(filterDraft);
    setPage(1);
  };

  const handleReset = () => {
    setFilterDraft(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  return (
    <div>
      <h2 className="section-title">审计日志</h2>

      <form onSubmit={handleSearch} className="card" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <div className="form-row" style={{ margin: 0 }}>
            <label>操作对象</label>
            <input
              value={filterDraft.subjectKey ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, subjectKey: e.target.value })}
              placeholder="如 222222222222#alice@x.com,子串匹配"
              style={{ width: 220 }}
            />
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>动作</label>
            <input
              value={filterDraft.action ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, action: e.target.value })}
              placeholder="如 UNSUBSCRIBE,子串匹配"
              style={{ width: 160 }}
            />
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>操作人</label>
            <input
              value={filterDraft.actor ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, actor: e.target.value })}
              placeholder="邮箱或系统任务名,子串匹配"
              style={{ width: 180 }}
            />
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>结果</label>
            <select
              value={filterDraft.result ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, result: e.target.value || undefined })}
            >
              <option value="">全部</option>
              {RESULTS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit">查询</button>
          <button type="button" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {logs.loading && <p>加载中...</p>}
      {logs.error && <div className="error-banner">{logs.error}</div>}

      {logs.data && (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>时间</th>
                <th>操作对象</th>
                <th>动作</th>
                <th>操作人</th>
                <th>结果</th>
                <th>详情</th>
              </tr>
            </thead>
            <tbody>
              {logs.data.items.map((item) => (
                <tr key={item.event_id}>
                  <td>{formatBeijingTime(item.event_time)}</td>
                  <td>{item.subject_key}</td>
                  <td>{item.action}</td>
                  <td>{item.actor}</td>
                  <td>{item.result}</td>
                  <td>{item.detail ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.data.items.length === 0 && <p style={{ color: "#64748b" }}>没有符合条件的记录</p>}
          <Pagination
            page={logs.data.page}
            totalPages={logs.data.total_pages}
            total={logs.data.total}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
